import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Calculator as CalcIcon, Ruler, Square } from "lucide-react";
import { PRODUCTS, SHEET_W, SHEET_H, waLink } from "./constants";
import { WhatsAppIcon } from "./Icons";
import { Reveal, Eyebrow } from "./Reveal";

const SHEET_AREA = SHEET_W * SHEET_H;
const LOSSES = [0, 5, 10, 15];
const num = (v) => {
  const n = parseFloat(String(v).replace(",", "."));
  return Number.isFinite(n) && n > 0 ? n : 0;
};
const fmt = (n, d = 2) => n.toLocaleString("pt-BR", { minimumFractionDigits: d, maximumFractionDigits: d });
const input = "w-full border-0 border-b border-white/20 bg-transparent px-0 py-3 font-display text-3xl font-bold text-white placeholder:text-stone-600 outline-none transition-colors focus:border-[#FACC15]";
const label = "font-mono-x text-[11px] uppercase tracking-[0.2em] text-stone-400";

const Tab = ({ active, onClick, icon: Icon, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid} className={`flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-3 font-display text-base font-bold uppercase tracking-wide transition-colors ${active ? "bg-[#FACC15] text-[#0c0a09]" : "text-stone-300 hover:text-white"}`}>
    <Icon className="h-4 w-4" /> {children}
  </button>
);

export const Calculator = () => {
  const [mode, setMode] = useState("medidas");
  const [largura, setLargura] = useState("");
  const [comprimento, setComprimento] = useState("");
  const [area, setArea] = useState("");
  const [perda, setPerda] = useState(10);
  const [produto, setProduto] = useState(PRODUCTS[0].name);

  const totalArea = mode === "medidas" ? num(largura) * num(comprimento) : num(area);
  const chapas = useMemo(() => (totalArea > 0 ? Math.ceil((totalArea * (1 + perda / 100)) / SHEET_AREA) : 0), [totalArea, perda]);

  const msg = `Olá, Destak! Usei a calculadora do site.\n\nProduto: ${produto}\nÁrea do projeto: ${fmt(totalArea)} m²\nMargem de perda: ${perda}%\nQuantidade estimada: ${chapas} chapa(s) de 1,22 x 2,44 m\n\nGostaria de um orçamento.`;

  return (
    <section id="calculadora" className="relative overflow-hidden bg-[#0c0a09] py-24 sm:py-32" data-testid="calculator-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
          <Reveal className="lg:col-span-7">
            <Eyebrow>Calculadora de chapas</Eyebrow>
            <h2 className="font-display mt-5 text-5xl font-black uppercase leading-[0.9] text-white sm:text-6xl lg:text-7xl">
              Quantas chapas <span className="text-[#FACC15]">você precisa?</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <p className="text-base text-stone-200 sm:text-lg">Informe as medidas do seu projeto e descubra na hora a quantidade de chapas.</p>
            <p className="font-mono-x mt-3 text-xs uppercase tracking-[0.2em] text-[#FACC15]">Chapa padrão Destak: 1,22 m × 2,44 m ({fmt(SHEET_AREA)} m²)</p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-px bg-white/10 lg:grid-cols-12">
          <Reveal className="bg-[#15100c] p-6 sm:p-10 lg:col-span-7">
            <div className="flex gap-1 rounded-full border border-white/10 p-1">
              <Tab active={mode === "medidas"} onClick={() => setMode("medidas")} icon={Ruler} testid="calc-mode-medidas">Medidas</Tab>
              <Tab active={mode === "area"} onClick={() => setMode("area")} icon={Square} testid="calc-mode-area">Área m²</Tab>
            </div>

            {mode === "medidas" ? (
              <div className="mt-10 grid grid-cols-2 gap-6">
                <label className="block"><span className={label}>Largura (m)</span>
                  <input value={largura} onChange={(e) => setLargura(e.target.value)} inputMode="decimal" placeholder="0,00" className={input} data-testid="calc-largura-input" />
                </label>
                <label className="block"><span className={label}>Comprimento (m)</span>
                  <input value={comprimento} onChange={(e) => setComprimento(e.target.value)} inputMode="decimal" placeholder="0,00" className={input} data-testid="calc-comprimento-input" />
                </label>
              </div>
            ) : (
              <label className="mt-10 block"><span className={label}>Área total do projeto (m²)</span>
                <input value={area} onChange={(e) => setArea(e.target.value)} inputMode="decimal" placeholder="0,00" className={input} data-testid="calc-area-input" />
              </label>
            )}

            <div className="mt-10">
              <span className={label}>Produto</span>
              <div className="mt-3 flex flex-wrap gap-2">
                {PRODUCTS.map((p) => (
                  <button key={p.slug} type="button" onClick={() => setProduto(p.name)} data-testid={`calc-produto-${p.slug}`} className={`rounded-full border px-4 py-2 text-sm transition-colors ${produto === p.name ? "border-[#FACC15] bg-[#FACC15] text-[#0c0a09]" : "border-white/20 text-stone-300 hover:border-[#FACC15]/60"}`}>
                    {p.name.replace("Compensado ", "")}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-10">
              <span className={label}>Margem de perda (recortes)</span>
              <div className="mt-3 grid grid-cols-4 gap-2">
                {LOSSES.map((l) => (
                  <button key={l} type="button" onClick={() => setPerda(l)} data-testid={`calc-perda-${l}`} className={`rounded-full border py-2 font-mono-x text-sm transition-colors ${perda === l ? "border-[#FACC15] bg-[#FACC15] text-[#0c0a09]" : "border-white/20 text-stone-300 hover:border-[#FACC15]/60"}`}>
                    {l}%
                  </button>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="relative flex flex-col justify-between bg-[#FACC15] p-6 text-[#0c0a09] sm:p-10 lg:col-span-5">
            <div>
              <p className="font-mono-x flex items-center gap-2 text-xs uppercase tracking-[0.2em]"><CalcIcon className="h-4 w-4" /> Resultado</p>
              <div className="mt-6 flex items-end gap-3">
                <motion.span key={chapas} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.4 }} className="font-display text-[7rem] font-black leading-[0.8] sm:text-[9rem]" data-testid="calc-result-chapas">
                  {chapas}
                </motion.span>
                <span className="font-display pb-2 text-2xl font-black uppercase leading-none">chapa{chapas === 1 ? "" : "s"}</span>
              </div>
              <dl className="mt-8 space-y-2 border-t-2 border-[#0c0a09] pt-5 text-sm">
                <div className="flex justify-between"><dt>Área do projeto</dt><dd className="font-bold" data-testid="calc-result-area">{fmt(totalArea)} m²</dd></div>
                <div className="flex justify-between"><dt>Com perda de {perda}%</dt><dd className="font-bold">{fmt(totalArea * (1 + perda / 100))} m²</dd></div>
                <div className="flex justify-between"><dt>Área coberta pelas chapas</dt><dd className="font-bold">{fmt(chapas * SHEET_AREA)} m²</dd></div>
              </dl>
              <p className="mt-4 text-xs text-stone-800">Estimativa baseada na chapa de 1,22 × 2,44 m. Confirme a quantidade com nossa equipe.</p>
            </div>
            <a href={chapas > 0 ? waLink(msg) : undefined} onClick={(e) => { if (!chapas) e.preventDefault(); }} target="_blank" rel="noopener noreferrer" aria-disabled={!chapas} data-testid="calc-whatsapp-btn" className={`btn-dark btn-lg mt-8 w-full ${chapas ? "" : "pointer-events-none opacity-40"}`}>
              <WhatsAppIcon className="h-5 w-5" /> Pedir orçamento dessas chapas
            </a>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
